import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Auth
import { CognitoAuthProvider } from '@/contexts';
import { ProtectedRoute } from '@/components/auth';

// Theme
import { ThemeProvider } from '@/contexts';

// Config
import { ConfigProvider, useConfig } from '@/contexts';
import { featureManager } from '@/services/featureManager/featureManager';

// Pages
import { Login } from './pages/Login';
import { Unauthorized } from './pages/Unauthorized';
import AuthError from './pages/AuthError/AuthError';
import Dashboard from './pages/Dashboard/Dashboard';
import Analysis from './pages/Analysis/Analysis';
import VblAnalysis from './pages/VblAnalysis/VblAnalysis';
import Forecast from './pages/Forecast/ForecastPage';
import Sites from './pages/Sites/Sites';
import Users from './pages/Users/Users';
import VBLConfig from './pages/VBLConfig/VBLConfig';
import VblForecast from './pages/VblForecast/VblForecastPage';
import FeatureDisabled from '@/pages/FeatureDisabled/FeatureDisabled';

// Layout
import Layout from './components/layout/Layout';

// Loading component
const ConfigLoading: React.FC = () => {
  const { isLoading, error } = useConfig();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading configuration...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Configuration Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Main app routes
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/auth-error" element={<AuthError />} />
      <Route path="/feature-disabled" element={<FeatureDisabled />} />

      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/analysis" element={<Analysis />} />
                <Route path="/forecast" element={<Forecast />} />
                <Route path="/forecast/:workspaceId" element={<Forecast />} />
                <Route path="/vbl-analysis" element={<ProtectedRoute featureEnabled={featureManager.canUseVBLFeature()}><VblAnalysis /></ProtectedRoute>} />
                <Route
                  path="/vbl-config"
                  element={
                    <ProtectedRoute featureEnabled={featureManager.canUseVBLFeature()}>
                      <VBLConfig />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vbl-forecast"
                  element={
                    <ProtectedRoute featureEnabled={featureManager.canUseVBLFeature()}>
                      <VblForecast />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sites"
                  element={
                    <ProtectedRoute>
                      <Sites />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

// App content that only renders when config is loaded
const AppContent: React.FC = () => {
  const { isLoading, error } = useConfig();

  if (isLoading || error) {
    return <ConfigLoading />;
  }

  return (
    <ThemeProvider>
      <CognitoAuthProvider>
        <AppRoutes />
      </CognitoAuthProvider>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider>
        <AppContent />
      </ConfigProvider>
    </QueryClientProvider>
  );
};

export default App;
